import { Fragment, useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Chip,
  Container,
  Group,
  Loader,
  Center,
  Progress,
  ScrollArea,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { IconCalendar, IconChevronDown, IconChevronRight, IconRefresh } from '@tabler/icons-react'
import { useRoutingMetrics } from '@/hooks/useAnalytics'

interface SourceNormRow {
  sourceNorm: string
  total: number
  ubsExclusion: number
  manualCharges: number
  aiAutomation: number
  auditor: number
  researcher: number
  missingInfo: number
  aiUnable: number
  workloadAutomationPct: number
}

interface ProductGroup {
  searchType: string
  total: number
  ubsExclusion: number
  manualCharges: number
  aiAutomation: number
  auditor: number
  researcher: number
  missingInfo: number
  aiUnable: number
  workloadAutomationPct: number
  sourceNorms: SourceNormRow[]
}

const DISPLAY_NAME: Record<string, string> = {
  Courts: 'Courthouses',
}
const displayName = (v: string) => DISPLAY_NAME[v] ?? v

function roundPcts(values: number[], total: number): string[] {
  if (total === 0) return values.map(() => '0.0')
  const raw = values.map(v => (v / total) * 1000)
  const floored = raw.map(r => Math.floor(r))
  let diff = 1000 - floored.reduce((s, f) => s + f, 0)
  const remainders = raw.map((r, i) => ({ i, rem: r - floored[i] }))
  remainders.sort((a, b) => b.rem - a.rem)
  for (const r of remainders) {
    if (diff <= 0) break
    floored[r.i] += 1
    diff--
  }
  return floored.map(f => (f / 10).toFixed(1))
}

const MISSING_INFO_PATTERNS = [
  'Disposition is missing or could not be determined',
  'Charge type is missing or could not be determined',
  'Charge is missing or could not be determined',
  'Charge description is missing or could not be determined',
  'Age at time of offense could not be calculated',
]

function isMissingInfoReason(reason: string): boolean {
  return MISSING_INFO_PATTERNS.some(p => reason.startsWith(p))
}

function computeBreakdown(bd: any, totalResearcher: number) {
  let missingInfo = 0
  let aiUnable = 0
  if (!bd) return { missingInfo, aiUnable }

  missingInfo += bd.adjudicationBanned || 0

  const validationReasons: Array<{ reason: string; count: number }> = bd.validationReasons || []
  let missingInfoFromValidation = 0
  for (const vr of validationReasons) {
    if (isMissingInfoReason(vr.reason)) missingInfoFromValidation += vr.count
  }
  const totalValidationReasonCount = validationReasons.reduce((s, vr) => s + vr.count, 0)
  const validationFailedOffenses = bd.validationFailed || 0
  if (totalValidationReasonCount > 0 && validationFailedOffenses > 0) {
    const missingRatio = missingInfoFromValidation / totalValidationReasonCount
    missingInfo += Math.round(validationFailedOffenses * missingRatio)
    aiUnable += Math.round(validationFailedOffenses * (1 - missingRatio))
  } else {
    aiUnable += validationFailedOffenses
  }
  missingInfo += bd.dispositionInProgress || 0
  aiUnable += bd.manualReview || 0
  aiUnable += bd.ambiguousClassification || 0
  aiUnable += bd.idOnly || 0
  aiUnable += bd.other || 0

  const totalBd = missingInfo + aiUnable
  if (totalBd > 0 && totalBd !== totalResearcher) {
    const scale = totalResearcher / totalBd
    missingInfo = Math.round(missingInfo * scale)
    aiUnable = totalResearcher - missingInfo
  }
  return { missingInfo, aiUnable }
}

const COLUMN_DEFS = [
  { value: 'totalOffenses', label: 'Total Offenses' },
  { value: 'pctOfVolume', label: '% of Volume' },
  { value: 'ubsExclusion', label: 'UBS Exclusion' },
  { value: 'manualCharges', label: 'Manual Charges' },
  { value: 'aiAutomation', label: 'QA Non-Reportable' },
  { value: 'auditor', label: 'QA Reportable' },
  { value: 'researcher', label: 'Researcher' },
  { value: 'missingInfo', label: 'Missing Info' },
  { value: 'aiUnable', label: 'AI Unable to Decide' },
  { value: 'workloadAutomation', label: 'Workload Automation' },
] as const

const DEFAULT_VISIBLE = COLUMN_DEFS
  .filter(c => !['totalOffenses', 'pctOfVolume', 'ubsExclusion', 'manualCharges', 'missingInfo', 'aiUnable'].includes(c.value))
  .map(c => c.value)

export function ProductAutomationSummaryPage() {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    new Date(),
  ])
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set())
  const [visibleCols, setVisibleCols] = useState<string[]>([...DEFAULT_VISIBLE])
  const show = (col: string) => visibleCols.includes(col)

  const filterParams = {
    startDate: dateRange[0]?.toISOString().split('T')[0] || '',
    endDate: dateRange[1]?.toISOString().split('T')[0] || '',
  }

  const { data: metricsData, isLoading, refetch } = useRoutingMetrics(filterParams)
  const rawSummary = metricsData?.metrics?.productAutomationSummary || []
  const bd = metricsData?.metrics?.insufficientDataBreakdown || null

  const productGroups: ProductGroup[] = useMemo(() => {
    const grouped = new Map<string, SourceNormRow[]>()

    for (const row of rawSummary as any[]) {
      const searchType = row.searchType ?? ''
      const aiAutomation = (row.aiExclusion ?? 0) + (row.aiAutomated ?? 0)
      const auditor = row.auditor ?? 0
      const researcher = row.researcher ?? 0
      const automated = aiAutomation + auditor
      const allManual = automated + researcher
      const workloadAutomationPct = allManual > 0
        ? Math.round((automated / allManual) * 100)
        : 0

      const manualCharges = allManual
      const normRow: SourceNormRow = {
        sourceNorm: row.sourceNorm ?? 'Unknown',
        total: row.total ?? 0,
        ubsExclusion: row.ubsExclusion ?? row.exclusion ?? 0,
        manualCharges,
        aiAutomation,
        auditor,
        researcher,
        missingInfo: 0,
        aiUnable: 0,
        workloadAutomationPct,
      }

      if (!grouped.has(searchType)) {
        grouped.set(searchType, [])
      }
      grouped.get(searchType)!.push(normRow)
    }

    const allNorms = Array.from(grouped.values()).flat()
    const grandResearcher = allNorms.reduce((s, n) => s + n.researcher, 0)
    const globalBd = computeBreakdown(bd, grandResearcher)

    for (const norm of allNorms) {
      if (grandResearcher > 0 && norm.researcher > 0) {
        const ratio = norm.researcher / grandResearcher
        norm.missingInfo = Math.round(globalBd.missingInfo * ratio)
        norm.aiUnable = norm.researcher - norm.missingInfo
      }
    }

    const groups: ProductGroup[] = []
    for (const [searchType, norms] of grouped) {
      const total = norms.reduce((s, n) => s + n.total, 0)
      const ubsExclusion = norms.reduce((s, n) => s + n.ubsExclusion, 0)
      const aiAutomation = norms.reduce((s, n) => s + n.aiAutomation, 0)
      const auditor = norms.reduce((s, n) => s + n.auditor, 0)
      const researcher = norms.reduce((s, n) => s + n.researcher, 0)
      const missingInfo = norms.reduce((s, n) => s + n.missingInfo, 0)
      const aiUnable = norms.reduce((s, n) => s + n.aiUnable, 0)
      const automated = aiAutomation + auditor
      const allManual = automated + researcher
      const manualCharges = allManual
      const workloadAutomationPct = allManual > 0
        ? Math.round((automated / allManual) * 100)
        : 0
      groups.push({ searchType, total, ubsExclusion, manualCharges, aiAutomation, auditor, researcher, missingInfo, aiUnable, workloadAutomationPct, sourceNorms: norms })
    }

    groups.sort((a, b) => b.total - a.total)
    return groups
  }, [rawSummary, bd])

  const totals = productGroups.reduce(
    (acc, g) => ({
      total: acc.total + g.total,
      ubsExclusion: acc.ubsExclusion + g.ubsExclusion,
      manualCharges: acc.manualCharges + g.manualCharges,
      aiAutomation: acc.aiAutomation + g.aiAutomation,
      auditor: acc.auditor + g.auditor,
      researcher: acc.researcher + g.researcher,
      missingInfo: acc.missingInfo + g.missingInfo,
      aiUnable: acc.aiUnable + g.aiUnable,
    }),
    { total: 0, ubsExclusion: 0, manualCharges: 0, aiAutomation: 0, auditor: 0, researcher: 0, missingInfo: 0, aiUnable: 0 }
  )
  const overallAutomated = totals.aiAutomation + totals.auditor
  const overallAll = overallAutomated + totals.researcher
  const overallWorkload = overallAll > 0
    ? Math.round((overallAutomated / overallAll) * 100)
    : 0

  interface SourceNormSummary {
    sourceNorm: string
    total: number
    pctOfTotal: number
    ubsExclusion: number
    manualCharges: number
    aiAutomation: number
    auditor: number
    researcher: number
    missingInfo: number
    aiUnable: number
    workloadAutomationPct: number
  }

  const sourceNormSummaries: SourceNormSummary[] = useMemo(() => {
    const byNorm = new Map<string, { total: number; ubsExclusion: number; aiAutomation: number; auditor: number; researcher: number; missingInfo: number; aiUnable: number }>()

    for (const group of productGroups) {
      for (const norm of group.sourceNorms) {
        const existing = byNorm.get(norm.sourceNorm) || { total: 0, ubsExclusion: 0, aiAutomation: 0, auditor: 0, researcher: 0, missingInfo: 0, aiUnable: 0 }
        existing.total += norm.total
        existing.ubsExclusion += norm.ubsExclusion
        existing.aiAutomation += norm.aiAutomation
        existing.auditor += norm.auditor
        existing.researcher += norm.researcher
        existing.missingInfo += norm.missingInfo
        existing.aiUnable += norm.aiUnable
        byNorm.set(norm.sourceNorm, existing)
      }
    }

    const grandTotal = totals.total
    return Array.from(byNorm.entries())
      .map(([sourceNorm, data]) => {
        const automated = data.aiAutomation + data.auditor
        const allManual = automated + data.researcher
        const manualCharges = allManual
        const workloadAutomationPct = allManual > 0
          ? Math.round((automated / allManual) * 100)
          : 0
        return {
          sourceNorm,
          total: data.total,
          pctOfTotal: grandTotal > 0 ? parseFloat(((data.total / grandTotal) * 100).toFixed(1)) : 0,
          ubsExclusion: data.ubsExclusion,
          manualCharges,
          aiAutomation: data.aiAutomation,
          auditor: data.auditor,
          researcher: data.researcher,
          missingInfo: data.missingInfo,
          aiUnable: data.aiUnable,
          workloadAutomationPct,
        }
      })
      .sort((a, b) => b.total - a.total)
  }, [productGroups, totals.total])

  const rawIdMatch = metricsData?.metrics?.idMatchByProduct || []

  interface IdMatchProductGroup {
    searchType: string
    total: number
    high: number
    medium: number
    low: number
    notEnoughInfo: number
    sourceNorms: Array<{
      sourceNorm: string
      total: number
      high: number
      medium: number
      low: number
      notEnoughInfo: number
    }>
  }

  const idMatchProductGroups: IdMatchProductGroup[] = useMemo(() => {
    const grouped = new Map<string, Map<string, { high: number; medium: number; low: number; notEnoughInfo: number; total: number }>>()

    for (const row of rawIdMatch as any[]) {
      const st = row.searchType ?? 'Unknown'
      const sn = row.sourceNorm ?? 'Unknown'
      const ml = String(row.matchLevel || '')
      const cnt = Number(row.count) || 0

      if (!grouped.has(st)) grouped.set(st, new Map())
      const normMap = grouped.get(st)!
      if (!normMap.has(sn)) normMap.set(sn, { high: 0, medium: 0, low: 0, notEnoughInfo: 0, total: 0 })
      const entry = normMap.get(sn)!
      entry.total += cnt
      if (ml === 'High') entry.high += cnt
      else if (ml === 'Medium') entry.medium += cnt
      // `low` bucket now holds the routing service's "Not Matching" level (no more "Low").
      else if (ml === 'Not Matching') entry.low += cnt
      else entry.notEnoughInfo += cnt
    }

    const groups: IdMatchProductGroup[] = []
    for (const [searchType, normMap] of grouped) {
      const norms = Array.from(normMap.entries()).map(([sourceNorm, d]) => ({ sourceNorm, ...d }))
      const total = norms.reduce((s, n) => s + n.total, 0)
      const high = norms.reduce((s, n) => s + n.high, 0)
      const medium = norms.reduce((s, n) => s + n.medium, 0)
      const low = norms.reduce((s, n) => s + n.low, 0)
      const notEnoughInfo = norms.reduce((s, n) => s + n.notEnoughInfo, 0)
      groups.push({ searchType, total, high, medium, low, notEnoughInfo, sourceNorms: norms.sort((a, b) => b.total - a.total) })
    }

    return groups.sort((a, b) => b.total - a.total)
  }, [rawIdMatch])

  const idMatchTotals = idMatchProductGroups.reduce(
    (acc, g) => ({
      total: acc.total + g.total,
      high: acc.high + g.high,
      medium: acc.medium + g.medium,
      low: acc.low + g.low,
      notEnoughInfo: acc.notEnoughInfo + g.notEnoughInfo,
    }),
    { total: 0, high: 0, medium: 0, low: 0, notEnoughInfo: 0 }
  )

  const [expandedIdMatchTypes, setExpandedIdMatchTypes] = useState<Set<string>>(new Set())

  const toggleIdMatchExpand = (searchType: string) => {
    setExpandedIdMatchTypes(prev => {
      const next = new Set(prev)
      if (next.has(searchType)) next.delete(searchType)
      else next.add(searchType)
      return next
    })
  }

  const toggleExpand = (searchType: string) => {
    setExpandedTypes(prev => {
      const next = new Set(prev)
      if (next.has(searchType)) next.delete(searchType)
      else next.add(searchType)
      return next
    })
  }

  const pct = (n: number, d: number) => d > 0 ? ((n / d) * 100).toFixed(1) : '0.0'

  return (
    <Container fluid p="md">
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <div>
            <Title order={2}>Automation</Title>
            <Text c="dimmed" size="sm">
              Per-search-type breakdown of offense routing outcomes and identity match levels.
              Click a row to expand source norm breakdown.
            </Text>
          </div>
          <Button leftSection={<IconRefresh size={16} />} onClick={() => refetch()}>
            Refresh
          </Button>
        </Group>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <DatePickerInput
            type="range"
            allowSingleDateInRange
            label="Date Range"
            placeholder="Select date range"
            value={dateRange}
            onChange={setDateRange}
            leftSection={<IconCalendar size={16} />}
            clearable
          />
        </Card>

        <Tabs defaultValue="automation">
          <Tabs.List>
            <Tabs.Tab value="automation">Workload Automation</Tabs.Tab>
            <Tabs.Tab value="idmatch">ID Match by Product</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="automation" pt="md">
            <Stack gap="lg">

        <Card shadow="sm" padding="sm" radius="md" withBorder>
          <Text size="sm" fw={500} mb={6}>Visible Columns</Text>
          <Chip.Group multiple value={visibleCols} onChange={setVisibleCols}>
            <Group gap="xs">
              {COLUMN_DEFS.map(c => (
                <Chip key={c.value} value={c.value} size="xs" variant="outline">{c.label}</Chip>
              ))}
            </Group>
          </Chip.Group>
        </Card>

        {!isLoading && sourceNormSummaries.length > 0 && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Title order={4} mb="md">Automation by Source</Title>
            <Table withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Source</Table.Th>
                  {show('totalOffenses') && <Table.Th style={{ textAlign: 'right' }}>Total Offenses</Table.Th>}
                  {show('pctOfVolume') && <Table.Th style={{ textAlign: 'right' }}>% of Volume</Table.Th>}
                  {show('ubsExclusion') && <Table.Th style={{ textAlign: 'right' }}>UBS Exclusion</Table.Th>}
                  {show('manualCharges') && <Table.Th style={{ textAlign: 'right' }}>Manual Charges</Table.Th>}
                  {show('aiAutomation') && <Table.Th style={{ textAlign: 'right' }}>QA Non-Reportable</Table.Th>}
                  {show('auditor') && <Table.Th style={{ textAlign: 'right' }}>QA Reportable</Table.Th>}
                  {show('researcher') && <Table.Th style={{ textAlign: 'right' }}>Researcher</Table.Th>}
                  {show('missingInfo') && <Table.Th style={{ textAlign: 'right' }}>Missing Info</Table.Th>}
                  {show('aiUnable') && <Table.Th style={{ textAlign: 'right' }}>AI Unable to Decide</Table.Th>}
                  {show('workloadAutomation') && <Table.Th style={{ textAlign: 'right', minWidth: 180 }}>Workload Automation<br /><Text size="xs" c="dimmed" component="span">(QA Non-Reportable + QA Reportable) / Total</Text></Table.Th>}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sourceNormSummaries.map((s) => {
                  const ubsPct = s.total > 0 ? ((s.ubsExclusion / s.total) * 100).toFixed(1) : '0.0'
                  const mcPct = s.total > 0 ? ((s.manualCharges / s.total) * 100).toFixed(1) : '0.0'
                  const [aiPct, audPct, resPct] = roundPcts([s.aiAutomation, s.auditor, s.researcher], s.manualCharges)
                  const miPct = s.researcher > 0 ? ((s.missingInfo / s.researcher) * 100).toFixed(1) : '0.0'
                  const auPct = s.researcher > 0 ? ((s.aiUnable / s.researcher) * 100).toFixed(1) : '0.0'
                  return (
                    <Table.Tr key={s.sourceNorm}>
                      <Table.Td fw={500}>{displayName(s.sourceNorm)}</Table.Td>
                      {show('totalOffenses') && (
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text fw={600}>{s.total.toLocaleString()}</Text>
                        </Table.Td>
                      )}
                      {show('pctOfVolume') && (
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text fw={600}>{s.pctOfTotal}%</Text>
                        </Table.Td>
                      )}
                      {show('ubsExclusion') && (
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Group gap={4} justify="flex-end">
                            <Text c="violet" fw={600}>{ubsPct}%</Text>
                            <Text size="xs" c="dimmed">({s.ubsExclusion.toLocaleString()})</Text>
                          </Group>
                        </Table.Td>
                      )}
                      {show('manualCharges') && (
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Group gap={4} justify="flex-end">
                            <Text c="blue" fw={600}>{mcPct}%</Text>
                            <Text size="xs" c="dimmed">({s.manualCharges.toLocaleString()})</Text>
                          </Group>
                        </Table.Td>
                      )}
                      {show('aiAutomation') && (
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Group gap={4} justify="flex-end">
                            <Text c="green" fw={600}>{aiPct}%</Text>
                            <Text size="xs" c="dimmed">({s.aiAutomation.toLocaleString()})</Text>
                          </Group>
                        </Table.Td>
                      )}
                      {show('auditor') && (
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Group gap={4} justify="flex-end">
                            <Text c="indigo" fw={600}>{audPct}%</Text>
                            <Text size="xs" c="dimmed">({s.auditor.toLocaleString()})</Text>
                          </Group>
                        </Table.Td>
                      )}
                      {show('researcher') && (
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Group gap={4} justify="flex-end">
                            <Text c="orange" fw={600}>{resPct}%</Text>
                            <Text size="xs" c="dimmed">({s.researcher.toLocaleString()})</Text>
                          </Group>
                        </Table.Td>
                      )}
                      {show('missingInfo') && (
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Group gap={4} justify="flex-end">
                            <Text c="pink" fw={600}>{miPct}%</Text>
                            <Text size="xs" c="dimmed">({s.missingInfo.toLocaleString()})</Text>
                          </Group>
                        </Table.Td>
                      )}
                      {show('aiUnable') && (
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Group gap={4} justify="flex-end">
                            <Text c="grape" fw={600}>{auPct}%</Text>
                            <Text size="xs" c="dimmed">({s.aiUnable.toLocaleString()})</Text>
                          </Group>
                        </Table.Td>
                      )}
                      {show('workloadAutomation') && (
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Group gap={6} justify="flex-end">
                            <Progress.Root size="lg" style={{ flex: 1, minWidth: 80 }}>
                              <Progress.Section
                                value={s.workloadAutomationPct}
                                color={s.workloadAutomationPct >= 20 ? 'green' : s.workloadAutomationPct >= 10 ? 'yellow' : 'gray'}
                              />
                            </Progress.Root>
                            <Badge
                              color={s.workloadAutomationPct >= 20 ? 'green' : s.workloadAutomationPct >= 10 ? 'yellow' : 'gray'}
                              variant="light"
                              size="lg"
                              style={{ minWidth: 50 }}
                            >
                              {s.workloadAutomationPct}%
                            </Badge>
                          </Group>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
              <Table.Tfoot style={{ background: '#F7FAFC' }}>
                {(() => {
                  const [tAiPct, tAudPct, tResPct] = roundPcts([totals.aiAutomation, totals.auditor, totals.researcher], totals.manualCharges)
                  return (
                <Table.Tr>
                  <Table.Td fw={700}>All Sources</Table.Td>
                  {show('totalOffenses') && (
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text fw={700}>{totals.total.toLocaleString()}</Text>
                    </Table.Td>
                  )}
                  {show('pctOfVolume') && (
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text fw={700}>100%</Text>
                    </Table.Td>
                  )}
                  {show('ubsExclusion') && (
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text c="violet" fw={700}>
                        {totals.total > 0 ? ((totals.ubsExclusion / totals.total) * 100).toFixed(1) : '0.0'}%
                      </Text>
                    </Table.Td>
                  )}
                  {show('manualCharges') && (
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text c="blue" fw={700}>
                        {totals.total > 0 ? ((totals.manualCharges / totals.total) * 100).toFixed(1) : '0.0'}%
                      </Text>
                    </Table.Td>
                  )}
                  {show('aiAutomation') && (
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text c="green" fw={700}>
                        {tAiPct}%
                      </Text>
                    </Table.Td>
                  )}
                  {show('auditor') && (
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text c="indigo" fw={700}>
                        {tAudPct}%
                      </Text>
                    </Table.Td>
                  )}
                  {show('researcher') && (
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text c="orange" fw={700}>
                        {tResPct}%
                      </Text>
                    </Table.Td>
                  )}
                  {show('missingInfo') && (
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text c="pink" fw={700}>
                        {totals.researcher > 0 ? ((totals.missingInfo / totals.researcher) * 100).toFixed(1) : '0.0'}%
                      </Text>
                    </Table.Td>
                  )}
                  {show('aiUnable') && (
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text c="grape" fw={700}>
                        {totals.researcher > 0 ? ((totals.aiUnable / totals.researcher) * 100).toFixed(1) : '0.0'}%
                      </Text>
                    </Table.Td>
                  )}
                  {show('workloadAutomation') && (
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Group gap={6} justify="flex-end">
                        <Progress.Root size="lg" style={{ flex: 1, minWidth: 80 }}>
                          <Progress.Section
                            value={overallWorkload}
                            color={overallWorkload >= 20 ? 'green' : overallWorkload >= 10 ? 'yellow' : 'gray'}
                          />
                        </Progress.Root>
                        <Badge
                          color={overallWorkload >= 20 ? 'green' : overallWorkload >= 10 ? 'yellow' : 'gray'}
                          variant="filled"
                          size="lg"
                          style={{ minWidth: 50 }}
                        >
                          {overallWorkload}%
                        </Badge>
                      </Group>
                    </Table.Td>
                  )}
                </Table.Tr>
                  )
                })()}
              </Table.Tfoot>
            </Table>
          </Card>
        )}

        {isLoading && !metricsData ? (
          <Center h={200}>
            <Loader size="lg" />
          </Center>
        ) : productGroups.length === 0 ? (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text c="dimmed" size="sm">No data available for the selected date range.</Text>
          </Card>
        ) : (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <ScrollArea>
              <Table striped highlightOnHover withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Product / Source Norm</Table.Th>
                    {show('totalOffenses') && <Table.Th style={{ textAlign: 'right' }}>Total Offenses</Table.Th>}
                    {show('ubsExclusion') && <Table.Th style={{ textAlign: 'right' }}>UBS Exclusion</Table.Th>}
                    {show('manualCharges') && <Table.Th style={{ textAlign: 'right' }}>Manual Charges</Table.Th>}
                    {show('aiAutomation') && <Table.Th style={{ textAlign: 'right' }}>QA Non-Reportable</Table.Th>}
                    {show('auditor') && <Table.Th style={{ textAlign: 'right' }}>QA Reportable</Table.Th>}
                    {show('researcher') && <Table.Th style={{ textAlign: 'right' }}>Researcher</Table.Th>}
                    {show('missingInfo') && <Table.Th style={{ textAlign: 'right' }}>Missing Info</Table.Th>}
                    {show('aiUnable') && <Table.Th style={{ textAlign: 'right' }}>AI Unable to Decide</Table.Th>}
                    {show('workloadAutomation') && <Table.Th style={{ textAlign: 'right' }}>Workload Automation<br /><Text size="xs" c="dimmed" component="span">(QA Non-Reportable + QA Reportable) / Total</Text></Table.Th>}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {productGroups.map((group) => {
                    const expanded = expandedTypes.has(group.searchType)
                    const pctOfTotal = totals.total > 0 ? ((group.total / totals.total) * 100).toFixed(1) : '0.0'
                    const ubsPct = group.total > 0 ? ((group.ubsExclusion / group.total) * 100).toFixed(1) : '0.0'
                    const mcPct = group.total > 0 ? ((group.manualCharges / group.total) * 100).toFixed(1) : '0.0'
                    const [aiPct, audPct, resPct] = roundPcts([group.aiAutomation, group.auditor, group.researcher], group.manualCharges)
                    const grpMiPct = group.researcher > 0 ? ((group.missingInfo / group.researcher) * 100).toFixed(1) : '0.0'
                    const grpAuPct = group.researcher > 0 ? ((group.aiUnable / group.researcher) * 100).toFixed(1) : '0.0'
                    return (
                      <Fragment key={group.searchType}>
                        <Table.Tr
                          style={{ cursor: 'pointer' }}
                          onClick={() => toggleExpand(group.searchType)}
                        >
                          <Table.Td fw={500}>
                            <Group gap={4}>
                              {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                              {displayName(group.searchType)}
                              <Badge size="xs" variant="light" color="gray">{group.sourceNorms.length} sources</Badge>
                            </Group>
                          </Table.Td>
                          {show('totalOffenses') && (
                            <Table.Td style={{ textAlign: 'right' }}>
                              <Group gap={4} justify="flex-end">
                                <Text fw={600}>{group.total.toLocaleString()}</Text>
                                <Text size="xs" c="dimmed">({pctOfTotal}%)</Text>
                              </Group>
                            </Table.Td>
                          )}
                          {show('ubsExclusion') && (
                            <Table.Td style={{ textAlign: 'right' }}>
                              <Group gap={4} justify="flex-end">
                                <Text c="violet" fw={600}>{ubsPct}%</Text>
                                <Text size="xs" c="dimmed">({group.ubsExclusion.toLocaleString()})</Text>
                              </Group>
                            </Table.Td>
                          )}
                          {show('manualCharges') && (
                            <Table.Td style={{ textAlign: 'right' }}>
                              <Group gap={4} justify="flex-end">
                                <Text c="blue" fw={600}>{mcPct}%</Text>
                                <Text size="xs" c="dimmed">({group.manualCharges.toLocaleString()})</Text>
                              </Group>
                            </Table.Td>
                          )}
                          {show('aiAutomation') && (
                            <Table.Td style={{ textAlign: 'right' }}>
                              <Group gap={4} justify="flex-end">
                                <Text c="green" fw={600}>{aiPct}%</Text>
                                <Text size="xs" c="dimmed">({group.aiAutomation.toLocaleString()})</Text>
                              </Group>
                            </Table.Td>
                          )}
                          {show('auditor') && (
                            <Table.Td style={{ textAlign: 'right' }}>
                              <Group gap={4} justify="flex-end">
                                <Text c="indigo" fw={600}>{audPct}%</Text>
                                <Text size="xs" c="dimmed">({group.auditor.toLocaleString()})</Text>
                              </Group>
                            </Table.Td>
                          )}
                          {show('researcher') && (
                            <Table.Td style={{ textAlign: 'right' }}>
                              <Group gap={4} justify="flex-end">
                                <Text c="orange" fw={600}>{resPct}%</Text>
                                <Text size="xs" c="dimmed">({group.researcher.toLocaleString()})</Text>
                              </Group>
                            </Table.Td>
                          )}
                          {show('missingInfo') && (
                            <Table.Td style={{ textAlign: 'right' }}>
                              <Group gap={4} justify="flex-end">
                                <Text c="pink" fw={600}>{grpMiPct}%</Text>
                                <Text size="xs" c="dimmed">({group.missingInfo.toLocaleString()})</Text>
                              </Group>
                            </Table.Td>
                          )}
                          {show('aiUnable') && (
                            <Table.Td style={{ textAlign: 'right' }}>
                              <Group gap={4} justify="flex-end">
                                <Text c="grape" fw={600}>{grpAuPct}%</Text>
                                <Text size="xs" c="dimmed">({group.aiUnable.toLocaleString()})</Text>
                              </Group>
                            </Table.Td>
                          )}
                          {show('workloadAutomation') && (
                            <Table.Td style={{ textAlign: 'right' }}>
                              <Badge
                                color={group.workloadAutomationPct >= 20 ? 'green' : group.workloadAutomationPct >= 10 ? 'yellow' : 'gray'}
                                variant="light"
                                size="lg"
                              >
                                {group.workloadAutomationPct}%
                              </Badge>
                            </Table.Td>
                          )}
                        </Table.Tr>
                        {expanded && group.sourceNorms.sort((a, b) => b.total - a.total).map((norm) => {
                          const normPctOfProduct = group.total > 0 ? ((norm.total / group.total) * 100).toFixed(1) : '0.0'
                          const normUbsPct = norm.total > 0 ? ((norm.ubsExclusion / norm.total) * 100).toFixed(1) : '0.0'
                          const normMcPct = norm.total > 0 ? ((norm.manualCharges / norm.total) * 100).toFixed(1) : '0.0'
                          const [normAiPct, normAudPct, normResPct] = roundPcts([norm.aiAutomation, norm.auditor, norm.researcher], norm.manualCharges)
                          const normMiPct = norm.researcher > 0 ? ((norm.missingInfo / norm.researcher) * 100).toFixed(1) : '0.0'
                          const normAuPct = norm.researcher > 0 ? ((norm.aiUnable / norm.researcher) * 100).toFixed(1) : '0.0'
                          return (
                            <Table.Tr key={`${group.searchType}-${norm.sourceNorm}`} style={{ background: '#f9fafb' }}>
                              <Table.Td pl={40}>
                                <Text size="sm" c="dimmed">{displayName(norm.sourceNorm)}</Text>
                              </Table.Td>
                              {show('totalOffenses') && (
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Group gap={4} justify="flex-end">
                                    <Text size="sm">{norm.total.toLocaleString()}</Text>
                                    <Text size="xs" c="dimmed">({normPctOfProduct}%)</Text>
                                  </Group>
                                </Table.Td>
                              )}
                              {show('ubsExclusion') && (
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Group gap={4} justify="flex-end">
                                    <Text size="sm" c="violet">{normUbsPct}%</Text>
                                    <Text size="xs" c="dimmed">({norm.ubsExclusion.toLocaleString()})</Text>
                                  </Group>
                                </Table.Td>
                              )}
                              {show('manualCharges') && (
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Group gap={4} justify="flex-end">
                                    <Text size="sm" c="blue">{normMcPct}%</Text>
                                    <Text size="xs" c="dimmed">({norm.manualCharges.toLocaleString()})</Text>
                                  </Group>
                                </Table.Td>
                              )}
                              {show('aiAutomation') && (
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Group gap={4} justify="flex-end">
                                    <Text size="sm" c="green">{normAiPct}%</Text>
                                    <Text size="xs" c="dimmed">({norm.aiAutomation.toLocaleString()})</Text>
                                  </Group>
                                </Table.Td>
                              )}
                              {show('auditor') && (
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Group gap={4} justify="flex-end">
                                    <Text size="sm" c="indigo">{normAudPct}%</Text>
                                    <Text size="xs" c="dimmed">({norm.auditor.toLocaleString()})</Text>
                                  </Group>
                                </Table.Td>
                              )}
                              {show('researcher') && (
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Group gap={4} justify="flex-end">
                                    <Text size="sm" c="orange">{normResPct}%</Text>
                                    <Text size="xs" c="dimmed">({norm.researcher.toLocaleString()})</Text>
                                  </Group>
                                </Table.Td>
                              )}
                              {show('missingInfo') && (
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Group gap={4} justify="flex-end">
                                    <Text size="sm" c="pink">{normMiPct}%</Text>
                                    <Text size="xs" c="dimmed">({norm.missingInfo.toLocaleString()})</Text>
                                  </Group>
                                </Table.Td>
                              )}
                              {show('aiUnable') && (
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Group gap={4} justify="flex-end">
                                    <Text size="sm" c="grape">{normAuPct}%</Text>
                                    <Text size="xs" c="dimmed">({norm.aiUnable.toLocaleString()})</Text>
                                  </Group>
                                </Table.Td>
                              )}
                              {show('workloadAutomation') && (
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Badge
                                    color={norm.workloadAutomationPct >= 20 ? 'green' : norm.workloadAutomationPct >= 10 ? 'yellow' : 'gray'}
                                    variant="outline"
                                    size="sm"
                                  >
                                    {norm.workloadAutomationPct}%
                                  </Badge>
                                </Table.Td>
                              )}
                            </Table.Tr>
                          )
                        })}
                      </Fragment>
                    )
                  })}
                </Table.Tbody>
                <Table.Tfoot style={{ background: '#F7FAFC' }}>
                  {(() => {
                    const ubsPct = totals.total > 0 ? ((totals.ubsExclusion / totals.total) * 100).toFixed(1) : '0.0'
                    const mcPct = totals.total > 0 ? ((totals.manualCharges / totals.total) * 100).toFixed(1) : '0.0'
                    const [aiPct, audPct, resPct] = roundPcts([totals.aiAutomation, totals.auditor, totals.researcher], totals.manualCharges)
                    return (
                      <Table.Tr>
                        <Table.Td fw={700}>All Products</Table.Td>
                        {show('totalOffenses') && (
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Group gap={4} justify="flex-end">
                              <Text fw={700}>{totals.total.toLocaleString()}</Text>
                              <Text size="xs" c="dimmed">(100%)</Text>
                            </Group>
                          </Table.Td>
                        )}
                        {show('ubsExclusion') && (
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Group gap={4} justify="flex-end">
                              <Text c="violet" fw={700}>{ubsPct}%</Text>
                              <Text size="xs" c="dimmed">({totals.ubsExclusion.toLocaleString()})</Text>
                            </Group>
                          </Table.Td>
                        )}
                        {show('manualCharges') && (
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Group gap={4} justify="flex-end">
                              <Text c="blue" fw={700}>{mcPct}%</Text>
                              <Text size="xs" c="dimmed">({totals.manualCharges.toLocaleString()})</Text>
                            </Group>
                          </Table.Td>
                        )}
                        {show('aiAutomation') && (
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Group gap={4} justify="flex-end">
                              <Text c="green" fw={700}>{aiPct}%</Text>
                              <Text size="xs" c="dimmed">({totals.aiAutomation.toLocaleString()})</Text>
                            </Group>
                          </Table.Td>
                        )}
                        {show('auditor') && (
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Group gap={4} justify="flex-end">
                              <Text c="indigo" fw={700}>{audPct}%</Text>
                              <Text size="xs" c="dimmed">({totals.auditor.toLocaleString()})</Text>
                            </Group>
                          </Table.Td>
                        )}
                        {show('researcher') && (
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Group gap={4} justify="flex-end">
                              <Text c="orange" fw={700}>{resPct}%</Text>
                              <Text size="xs" c="dimmed">({totals.researcher.toLocaleString()})</Text>
                            </Group>
                          </Table.Td>
                        )}
                        {show('missingInfo') && (
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Group gap={4} justify="flex-end">
                              <Text c="pink" fw={700}>
                                {totals.researcher > 0 ? ((totals.missingInfo / totals.researcher) * 100).toFixed(1) : '0.0'}%
                              </Text>
                              <Text size="xs" c="dimmed">({totals.missingInfo.toLocaleString()})</Text>
                            </Group>
                          </Table.Td>
                        )}
                        {show('aiUnable') && (
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Group gap={4} justify="flex-end">
                              <Text c="grape" fw={700}>
                                {totals.researcher > 0 ? ((totals.aiUnable / totals.researcher) * 100).toFixed(1) : '0.0'}%
                              </Text>
                              <Text size="xs" c="dimmed">({totals.aiUnable.toLocaleString()})</Text>
                            </Group>
                          </Table.Td>
                        )}
                        {show('workloadAutomation') && (
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Badge
                              color={overallWorkload >= 20 ? 'green' : overallWorkload >= 10 ? 'yellow' : 'gray'}
                              variant="filled"
                              size="lg"
                            >
                              {overallWorkload}%
                            </Badge>
                          </Table.Td>
                        )}
                      </Table.Tr>
                    )
                  })()}
                </Table.Tfoot>
              </Table>
            </ScrollArea>
          </Card>
        )}

            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="idmatch" pt="md">
            <Stack gap="lg">
              {isLoading && !metricsData ? (
                <Center h={200}>
                  <Loader size="lg" />
                </Center>
              ) : idMatchProductGroups.length === 0 ? (
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Text c="dimmed" size="sm">No identity match data available for the selected date range.</Text>
                </Card>
              ) : (
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <ScrollArea>
                    <Table striped highlightOnHover withTableBorder withColumnBorders>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Product / Source</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Total</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>High</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Medium</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Not Matching</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Not Enough Info</Table.Th>
                          <Table.Th style={{ textAlign: 'right', minWidth: 180 }}>Match Distribution</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {idMatchProductGroups.map((group) => {
                          const expanded = expandedIdMatchTypes.has(group.searchType)
                          return (
                            <Fragment key={group.searchType}>
                              <Table.Tr style={{ cursor: 'pointer' }} onClick={() => toggleIdMatchExpand(group.searchType)}>
                                <Table.Td fw={500}>
                                  <Group gap={4}>
                                    {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                                    {displayName(group.searchType)}
                                    <Badge size="xs" variant="light" color="gray">{group.sourceNorms.length} sources</Badge>
                                  </Group>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Text fw={600}>{group.total.toLocaleString()}</Text>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Group gap={4} justify="flex-end">
                                    <Text c="green" fw={600}>{pct(group.high, group.total)}%</Text>
                                    <Text size="xs" c="dimmed">({group.high.toLocaleString()})</Text>
                                  </Group>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Group gap={4} justify="flex-end">
                                    <Text c="orange" fw={600}>{pct(group.medium, group.total)}%</Text>
                                    <Text size="xs" c="dimmed">({group.medium.toLocaleString()})</Text>
                                  </Group>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Group gap={4} justify="flex-end">
                                    <Text c="red" fw={600}>{pct(group.low, group.total)}%</Text>
                                    <Text size="xs" c="dimmed">({group.low.toLocaleString()})</Text>
                                  </Group>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Group gap={4} justify="flex-end">
                                    <Text c="gray" fw={600}>{pct(group.notEnoughInfo, group.total)}%</Text>
                                    <Text size="xs" c="dimmed">({group.notEnoughInfo.toLocaleString()})</Text>
                                  </Group>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Progress.Root size="lg" style={{ minWidth: 120 }}>
                                    <Progress.Section value={group.total > 0 ? (group.high / group.total) * 100 : 0} color="green" />
                                    <Progress.Section value={group.total > 0 ? (group.medium / group.total) * 100 : 0} color="orange" />
                                    <Progress.Section value={group.total > 0 ? (group.low / group.total) * 100 : 0} color="red" />
                                    <Progress.Section value={group.total > 0 ? (group.notEnoughInfo / group.total) * 100 : 0} color="gray" />
                                  </Progress.Root>
                                </Table.Td>
                              </Table.Tr>
                              {expanded && group.sourceNorms.map((norm) => (
                                <Table.Tr key={`${group.searchType}-${norm.sourceNorm}`} style={{ background: '#f9fafb' }}>
                                  <Table.Td pl={40}>
                                    <Text size="sm" c="dimmed">{displayName(norm.sourceNorm)}</Text>
                                  </Table.Td>
                                  <Table.Td style={{ textAlign: 'right' }}>
                                    <Text size="sm">{norm.total.toLocaleString()}</Text>
                                  </Table.Td>
                                  <Table.Td style={{ textAlign: 'right' }}>
                                    <Group gap={4} justify="flex-end">
                                      <Text size="sm" c="green">{pct(norm.high, norm.total)}%</Text>
                                      <Text size="xs" c="dimmed">({norm.high.toLocaleString()})</Text>
                                    </Group>
                                  </Table.Td>
                                  <Table.Td style={{ textAlign: 'right' }}>
                                    <Group gap={4} justify="flex-end">
                                      <Text size="sm" c="orange">{pct(norm.medium, norm.total)}%</Text>
                                      <Text size="xs" c="dimmed">({norm.medium.toLocaleString()})</Text>
                                    </Group>
                                  </Table.Td>
                                  <Table.Td style={{ textAlign: 'right' }}>
                                    <Group gap={4} justify="flex-end">
                                      <Text size="sm" c="red">{pct(norm.low, norm.total)}%</Text>
                                      <Text size="xs" c="dimmed">({norm.low.toLocaleString()})</Text>
                                    </Group>
                                  </Table.Td>
                                  <Table.Td style={{ textAlign: 'right' }}>
                                    <Group gap={4} justify="flex-end">
                                      <Text size="sm" c="gray">{pct(norm.notEnoughInfo, norm.total)}%</Text>
                                      <Text size="xs" c="dimmed">({norm.notEnoughInfo.toLocaleString()})</Text>
                                    </Group>
                                  </Table.Td>
                                  <Table.Td style={{ textAlign: 'right' }}>
                                    <Progress.Root size="sm" style={{ minWidth: 120 }}>
                                      <Progress.Section value={norm.total > 0 ? (norm.high / norm.total) * 100 : 0} color="green" />
                                      <Progress.Section value={norm.total > 0 ? (norm.medium / norm.total) * 100 : 0} color="orange" />
                                      <Progress.Section value={norm.total > 0 ? (norm.low / norm.total) * 100 : 0} color="red" />
                                      <Progress.Section value={norm.total > 0 ? (norm.notEnoughInfo / norm.total) * 100 : 0} color="gray" />
                                    </Progress.Root>
                                  </Table.Td>
                                </Table.Tr>
                              ))}
                            </Fragment>
                          )
                        })}
                      </Table.Tbody>
                      <Table.Tfoot style={{ background: '#F7FAFC' }}>
                        <Table.Tr>
                          <Table.Td fw={700}>All Products</Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Text fw={700}>{idMatchTotals.total.toLocaleString()}</Text>
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Text c="green" fw={700}>{pct(idMatchTotals.high, idMatchTotals.total)}%</Text>
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Text c="orange" fw={700}>{pct(idMatchTotals.medium, idMatchTotals.total)}%</Text>
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Text c="red" fw={700}>{pct(idMatchTotals.low, idMatchTotals.total)}%</Text>
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Text c="gray" fw={700}>{pct(idMatchTotals.notEnoughInfo, idMatchTotals.total)}%</Text>
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Progress.Root size="lg" style={{ minWidth: 120 }}>
                              <Progress.Section value={idMatchTotals.total > 0 ? (idMatchTotals.high / idMatchTotals.total) * 100 : 0} color="green" />
                              <Progress.Section value={idMatchTotals.total > 0 ? (idMatchTotals.medium / idMatchTotals.total) * 100 : 0} color="orange" />
                              <Progress.Section value={idMatchTotals.total > 0 ? (idMatchTotals.low / idMatchTotals.total) * 100 : 0} color="red" />
                              <Progress.Section value={idMatchTotals.total > 0 ? (idMatchTotals.notEnoughInfo / idMatchTotals.total) * 100 : 0} color="gray" />
                            </Progress.Root>
                          </Table.Td>
                        </Table.Tr>
                      </Table.Tfoot>
                    </Table>
                  </ScrollArea>

                  <Group gap="lg" mt="md">
                    <Group gap={6}><div style={{ width: 12, height: 12, borderRadius: 2, background: '#2ECC71' }} /><Text size="xs">High</Text></Group>
                    <Group gap={6}><div style={{ width: 12, height: 12, borderRadius: 2, background: '#F39C12' }} /><Text size="xs">Medium</Text></Group>
                    <Group gap={6}><div style={{ width: 12, height: 12, borderRadius: 2, background: '#E74C3C' }} /><Text size="xs">Not Matching</Text></Group>
                    <Group gap={6}><div style={{ width: 12, height: 12, borderRadius: 2, background: '#95A5A6' }} /><Text size="xs">Not Enough Info</Text></Group>
                  </Group>
                </Card>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  )
}
